import React from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { CheckIcon, AlertTriangleIcon } from "lucide-react";

/** Mirrors the backend MigrationRecordOutcome (dto/MigrationRecordOutcome.java). */
export interface MigrationRecordOutcome {
  result: "PROCESSED" | "SKIPPED" | "FAILED" | string;
  recordType?: string | null;
  recordId?: string | null;
  externalId?: string | null;
  displayName?: string | null;
  objectTypeName?: string | null;
  reason?: string | null;
  error?: string | null;
}

/** Mirrors the backend MigrationResult (dto/MigrationResult.java), minus objectTypes. */
export interface MigrationOutcomeSummary {
  workflowId?: number | null;
  total: number;
  processed: number;
  skipped: number;
  failed: number;
  message?: string | null;
  outcomes?: MigrationRecordOutcome[] | null;
}

const RECORD_TYPE_LABELS: Record<string, string> = {
  OBJECT_TYPE: "Object Type",
  PROPERTY: "Property",
  RELATION: "Relation",
  PROPERTY_OPTIONS: "Property Option",
  OBJECT: "Object Instance",
};

const RESULT_ORDER: Record<string, number> = { FAILED: 0, SKIPPED: 1, PROCESSED: 2 };

function resultBadge(result: string) {
  switch (result) {
    case "PROCESSED":
      return <Badge className="bg-green-600/15 text-green-700 dark:text-green-400 hover:bg-green-600/15 border-transparent">Migrated</Badge>;
    case "SKIPPED":
      return <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 hover:bg-amber-500/15 border-transparent">Skipped</Badge>;
    case "FAILED":
      return <Badge variant="destructive">Failed</Badge>;
    default:
      return <Badge variant="outline">{result}</Badge>;
  }
}

const OutcomeRow: React.FC<{ outcome: MigrationRecordOutcome }> = ({ outcome }) => {
  const name = outcome.displayName || outcome.externalId || "Unnamed record";
  const typeLabel = outcome.recordType ? (RECORD_TYPE_LABELS[outcome.recordType] || outcome.recordType) : "Record";

  return (
    <li className="border rounded-md p-3 bg-card">
      <div className="flex items-center gap-2 flex-wrap">
        {resultBadge(outcome.result)}
        <span className="text-sm font-medium text-foreground truncate">{name}</span>
        <span className="text-xs text-muted-foreground">
          {typeLabel}
          {outcome.objectTypeName ? ` · ${outcome.objectTypeName}` : ""}
        </span>
      </div>
      {outcome.reason && (
        <p className="text-sm text-muted-foreground mt-1.5">{outcome.reason}</p>
      )}
      {outcome.result === "FAILED" && outcome.error && (
        <p className="text-sm text-destructive mt-1">{outcome.error}</p>
      )}
    </li>
  );
};

/**
 * Persistent migration result view: summary counts plus one row per record with its outcome,
 * reason, and (for failures) the backend error — so a failed run explains itself instead of
 * flashing a toast and rendering a generic success screen.
 */
export const MigrationOutcomePanel: React.FC<{
  summary: MigrationOutcomeSummary;
  onReset: () => void;
}> = ({ summary, onReset }) => {
  const hasFailures = summary.failed > 0;
  const outcomes = [...(summary.outcomes ?? [])].sort(
    (a, b) => (RESULT_ORDER[a.result] ?? 3) - (RESULT_ORDER[b.result] ?? 3)
  );

  return (
    <div className="flex flex-col items-center py-8 px-4">
      <div className={`rounded-full p-4 mb-4 ${hasFailures ? "bg-destructive/10" : "bg-primary/10"}`}>
        {hasFailures
          ? <AlertTriangleIcon className="h-12 w-12 text-destructive" />
          : <CheckIcon className="h-12 w-12 text-primary" />}
      </div>
      <h2 className="text-xl font-semibold text-foreground mb-1">
        {hasFailures ? "Migration completed with failures" : "Migration Completed Successfully"}
      </h2>
      <p className="text-muted-foreground mb-4 text-sm">
        {summary.processed} migrated · {summary.skipped} skipped · {summary.failed} failed (of {summary.total})
      </p>

      {outcomes.length > 0 && (
        <ul className="w-full max-w-3xl space-y-2 mb-8 text-left">
          {outcomes.map((o, i) => (
            <OutcomeRow key={`${o.recordId ?? "r"}-${i}`} outcome={o} />
          ))}
        </ul>
      )}

      <Button onClick={onReset} variant="default" className="px-6">
        Start New Migration
      </Button>
    </div>
  );
};

export default MigrationOutcomePanel;